// skillgenpro-frontend/src/error-framework/logger.js
//
// Centralised frontend logging. Before this existed, the only record of a
// client-side failure was whatever console.error a given component happened
// to call — unstructured, unsearchable, and (in production) pure noise for
// anyone with devtools open.
//
// Design rules:
//  1. Logging must NEVER throw. Every public function is internally guarded;
//     a logger that crashes inside an error handler turns a recoverable
//     error into a blank page, which is the exact failure this framework
//     exists to prevent.
//  2. Development gets full detail. Production gets a single structured
//     line and no stack traces — stacks are retained in the payload for a
//     future transport, not printed for end users.
//  3. Transport-agnostic. `setLogTransport()` lets Sentry / CloudWatch RUM /
//     Datadog / App Insights be wired in later without touching a single
//     call site.

const IS_PROD = process.env.NODE_ENV === 'production';

// One correlation id per page-load. Every log line from this session shares
// it, so a user reporting "it broke" can be tied to a full trail.
const SESSION_CORRELATION_ID = (() => {
  try {
    return 'fe_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 10);
  } catch (e) {
    return 'fe_unknown';
  }
})();

let transport = null;

/**
 * Register an external sink (Sentry, CloudWatch RUM, Datadog, App Insights).
 * The sink receives the same structured record printed to the console.
 * A throwing transport is swallowed and disabled rather than propagated.
 */
export const setLogTransport = (fn) => {
  transport = typeof fn === 'function' ? fn : null;
};

export const getCorrelationId = () => SESSION_CORRELATION_ID;

export const newErrorId = (prefix) => {
  try {
    return (prefix || 'err') + '_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8);
  } catch (e) {
    return (prefix || 'err') + '_unknown';
  }
};

// Best-effort user id. Deliberately tolerant: this runs inside error paths
// where localStorage may be corrupt, which is itself one of the crash
// classes this framework guards against.
const currentUserId = () => {
  try {
    const direct = localStorage.getItem('user_id');
    if (direct) return direct;
    const raw = localStorage.getItem('user');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return (parsed && (parsed.id != null ? parsed.id : parsed.user_id)) || null;
  } catch (e) {
    return null;
  }
};

const buildRecord = (level, event, detail) => {
  const d = detail || {};
  let route;
  let url;
  try {
    route = window.location.pathname;
    url = window.location.href;
  } catch (e) {
    /* non-browser context */
  }
  return {
    level,
    event,
    errorId: d.errorId || null,
    correlationId: SESSION_CORRELATION_ID,
    route,
    url,
    component: d.component || null,
    api: d.api || null,
    status: d.status != null ? d.status : null,
    message: d.message || null,
    stack: d.stack || null,
    componentStack: d.componentStack || null,
    userId: currentUserId(),
    userAgent: (typeof navigator !== 'undefined' && navigator.userAgent) || null,
    online: typeof navigator !== 'undefined' ? navigator.onLine : null,
    timestamp: new Date().toISOString(),
  };
};

const emit = (level, event, detail) => {
  let record;
  try {
    record = buildRecord(level, event, detail);
  } catch (e) {
    return null;
  }

  try {
    if (transport) transport(record);
  } catch (e) {
    // A broken sink must not break the app — drop it and carry on.
    transport = null;
  }

  try {
    if (IS_PROD) {
      // Production: one structured line, no stack, no spam. Warnings and
      // below are dropped entirely so devtools stays readable.
      if (level === 'error') {
        const { stack, componentStack, ...safe } = record;
        // eslint-disable-next-line no-console
        console.error('[tsg]', JSON.stringify(safe));
      }
    } else {
      const fn = level === 'error' ? console.error : level === 'warn' ? console.warn : console.info;
      // eslint-disable-next-line no-console
      fn('[tsg:' + event + ']', record);
    }
  } catch (e) {
    /* console itself failed — nothing further we can safely do */
  }

  return record;
};

export const logError = (event, detail) => emit('error', event, detail);
export const logWarn = (event, detail) => emit('warn', event, detail);
export const logInfo = (event, detail) => emit('info', event, detail);

export default { logError, logWarn, logInfo, setLogTransport, getCorrelationId, newErrorId };
